import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Bars } from "react-loader-spinner";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../../../Shared/axiosInstance.js";

export default function Products() {
  const queryClient = useQueryClient();

  // حالة الفلاتر و pagination
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // جلب الأقسام
  const { data: categoriesData, isLoading: categoriesLoading, isError: categoriesError } = useQuery(
    ["categories"],
    async () => {
      const { data } = await axiosInstance.get("/Category");
      return data?.data || [];
    },
    { staleTime: 1000 * 60 * 5 }
  );

  // جلب العلامات التجارية
  const { data: brandsData, isLoading: brandsLoading, isError: brandsError } = useQuery(
    ["brands"],
    async () => {
      const { data } = await axiosInstance.get("/Brand");
      return data?.data || [];
    },
    { staleTime: 1000 * 60 * 5 }
  );

  // جلب كل المنتجات (بدون pagination من API)
  const { data: allProductsData, isLoading: productsLoading, isError: productsError, refetch: refetchProducts } = useQuery(
    ["products"],
    async () => {
      const { data } = await axiosInstance.get("/Product");
      return Array.isArray(data?.data) ? data.data : [];
    }
   
  );

  // ✅ فلترة وترتيب المنتجات في الفرونت
  const filteredAndSortedProducts = useMemo(() => {
    if (!allProductsData) return [];
    
    let filtered = [...allProductsData];

    // فلترة حسب القسم
    if (selectedCategory) {
      const category = categoriesData?.find(c => c.id === selectedCategory);
      if (category) {
        filtered = filtered.filter(p => p.categoryName === category.name);
      }
    }

    // فلترة حسب العلامة التجارية
    if (selectedBrand) {
      const brand = brandsData?.find(b => b.id === selectedBrand);
      if (brand) {
        filtered = filtered.filter(p => p.brandName === brand.name);
      }
    }

    // فلترة حسب البحث (اسم أو وصف)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(search) || 
        p.description?.toLowerCase().includes(search)
      );
    }

    // ترتيب
    if (sortBy) {
      filtered.sort((a, b) => {
        let aVal, bVal;
        if (sortBy === "name") {
          aVal = a.name?.toLowerCase() || "";
          bVal = b.name?.toLowerCase() || "";
        } else if (sortBy === "quantity") {
          aVal = a.quantityInStock || 0;
          bVal = b.quantityInStock || 0;
        } else if (sortBy === "sellprice") {
          aVal = a.sellPrice || 0;
          bVal = b.sellPrice || 0;
        } else if (sortBy === "buyprice") {
          aVal = a.buyPrice || 0;
          bVal = b.buyPrice || 0;
        }
        
        if (sortOrder === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [allProductsData, selectedCategory, selectedBrand, searchTerm, sortBy, sortOrder, categoriesData, brandsData]);

  // ✅ Pagination في الفرونت
  const totalCount = filteredAndSortedProducts.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentProducts = filteredAndSortedProducts.slice(startIndex, endIndex);

  // حذف منتج
  const deleteMutation = useMutation(
    async (id) => await axiosInstance.delete(`/Product/${id}`),
    {
      onSuccess: (data) => {
        Swal.fire({
          title: "تم الحذف!",
          text: data.message || "تم حذف المنتج بنجاح.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        queryClient.invalidateQueries("products");
      },
      onError: (err) => {
        Swal.fire({
          title: "خطأ!",
          text: err.response?.data?.message || "فشل في حذف المنتج.",
          icon: "error",
        });
      },
    }
  );

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "هل أنت متأكد؟",
      text: "لن تتمكن من التراجع عن هذا!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "نعم، احذفه!",
      cancelButtonText: "إلغاء",
    });
    if (result.isConfirmed) deleteMutation.mutate(id);
  };

  // Handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };
  
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };
  
  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  };
  
  const handleBrandChange = (value) => {
    setSelectedBrand(value);
    setCurrentPage(1);
  };
  
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };
  
  const handleSortByChange = (value) => {
    setSortBy(value);
    setCurrentPage(1);
  };
  
  const handleSortOrderChange = (value) => {
    setSortOrder(value);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSelectedCategory("");
    setSelectedBrand("");
    setSearchTerm("");
    setSortBy("");
    setSortOrder("asc");
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  if (productsLoading || categoriesLoading || brandsLoading) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <Bars height="80" width="80" color="#2563eb" ariaLabel="loading" />
        <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>جارٍ تحميل المنتجات...</p>
      </div>
    );
  }

  if (productsError || categoriesError || brandsError) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <p style={{ color: "#dc2626", fontSize: "18px" }}>فشل في تحميل البيانات</p>
        <button
          onClick={() => {
            refetchProducts();
            queryClient.invalidateQueries("categories");
            queryClient.invalidateQueries("brands");
          }}
          style={{ background: "#2563eb", color: "#fff", padding: "10px 20px", borderRadius: "6px", border: "none", fontWeight: 600, fontSize: "15px", cursor: "pointer" }}
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  const categories = categoriesData || [];
  const brands = brandsData || [];
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  return (
 <div className="container py-5" style={{ maxWidth: "1400px" }}>
    {/* Header */}
    <div className="d-flex justify-content-between align-items-center mb-4 mt-3 pt-3">
      <div>
        <h2 style={{ color: "#2563eb", fontWeight: 700, fontSize: 28 }}>قائمة المنتجات</h2>
        <small style={{ color: "#64748b", fontSize: 14 }}>
          عرض {currentProducts.length} من أصل {totalCount} منتج
          {allProductsData && ` (إجمالي: ${allProductsData.length})`}
        </small>
      </div>
      <Link to="/add-product">
        <button style={{ background: "#2563eb", color: "#fff", padding: "10px 20px", borderRadius: 6, border: "none", fontWeight: 600, fontSize: 15, boxShadow: "0 2px 8px rgba(37, 99, 235, 0.3)" }}>
          <i className="bi bi-plus-circle me-1"></i> إضافة منتج
        </button>
      </Link>
    </div>

      {/* Filters */}
      <div className="mb-4" style={{ background: "#fff", border: "1px solid #dbeafe", borderRadius: 8, padding: 20, boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)" }}>
        <h5 style={{ color: "#1e40af", fontWeight: 600, fontSize: 16, marginBottom: 16 }}>خيارات التصفية والترتيب</h5>
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label style={labelStyle}>البحث (اسم أو وصف)</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="ابحث عن منتج..."
              style={selectStyle}
            />
          </div>
          <div className="col-md-2">
            <label style={labelStyle}>القسم</label>
            <select value={selectedCategory} onChange={(e) => handleCategoryChange(e.target.value)} style={selectStyle}>
              <option value="">كل الأقسام</option>
              {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label style={labelStyle}>العلامة التجارية</label>
            <select value={selectedBrand} onChange={(e) => handleBrandChange(e.target.value)} style={selectStyle}>
              <option value="">كل العلامات</option>
              {brands.map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label style={labelStyle}>ترتيب حسب</label>
            <select value={sortBy} onChange={(e) => handleSortByChange(e.target.value)} style={selectStyle}>
              <option value="">افتراضي</option>
              <option value="name">الاسم</option>
              <option value="quantity">الكمية</option>
              <option value="sellprice">سعر البيع</option>
              <option value="buyprice">سعر الشراء</option>
            </select>
          </div>
          <div className="col-md-1">
            <label style={labelStyle}>الترتيب</label>
            <select value={sortOrder} onChange={(e) => handleSortOrderChange(e.target.value)} disabled={!sortBy} style={{ ...selectStyle, cursor: sortBy ? "pointer" : "not-allowed", opacity: sortBy ? 1 : 0.6 }}>
              <option value="asc">⬆️</option>
              <option value="desc">⬇️</option>
            </select>
          </div>
          <div className="col-md-2">
            <button onClick={handleReset} style={{ ...buttonStyle("#64748b"), width: "100%" }}>إعادة الضبط</button>
          </div>
        </div>
      </div>

      {/* عدد الصفوف */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <label style={{ ...labelStyle, display: "inline", marginLeft: "8px" }}>عدد الصفوف:</label>
          <select value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))} style={{ ...selectStyle, width: "auto", display: "inline-block" }}>
            {[5, 10, 15, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Products Table */}
      {currentProducts.length > 0 ? (
        <div className="table-responsive">
          <table className="table mb-0" style={tableStyle}>
            <thead style={{ background: "#dbeafe" }}>
              <tr>
                {["#", "الاسم", "الوصف", "القسم", "العلامة التجارية", "سعر البيع", "سعر الشراء", "الكمية", "إجراءات"].map((th, i) => <th key={i} style={thStyle}>{th}</th>)}
              </tr>
            </thead>
            <tbody>
              {currentProducts.map((pr, i) => (
                <tr key={pr.id} style={{ background: i % 2 === 0 ? "#f8fafc" : "#ffffff" }}>
                  <td style={tdStyle}>{startIndex + i + 1}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: "#2563eb" }}>{pr.name}</td>
                  <td style={tdStyle}>{pr.description || "-"}</td>
                  <td style={tdStyle}>{pr.categoryName || "-"}</td>
                  <td style={tdStyle}>{pr.brandName || "-"}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: "#16a34a" }}>{pr.sellPrice} EGP</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: "#dc2626" }}>{pr.buyPrice} EGP</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: pr.quantityInStock === 0 ? "#dc2626" : "#16a34a" }}>{pr.quantityInStock}</td>
                  <td style={tdStyle}>
                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                      <Link to={`/update-product/${pr.id}`}><button style={actionBtnStyle("#2563eb")}>تعديل</button></Link>
                      <button onClick={() => handleDelete(pr.id)} style={actionBtnStyle("#dc2626")} disabled={deleteMutation.isLoading}>حذف</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 60, background: "#fff", borderRadius: 8, border: "1px solid #dbeafe" }}>
          <h4 style={{ color: "#475569", fontWeight: 600, marginBottom: 8 }}>لا توجد منتجات</h4>
          <p style={{ color: "#64748b", fontSize: 15 }}>
            {selectedCategory || selectedBrand || searchTerm ? "جرب تغيير الفلاتر" : "ابدأ بإضافة أول منتج لك"}
          </p>
          {!selectedCategory && !selectedBrand && !searchTerm && (
            <Link to="/add-product"><button style={{ background: "#2563eb", color: "#fff", padding: "10px 20px", borderRadius: 6, border: "none", fontWeight: 600, fontSize: 15 }}>إضافة منتج</button></Link>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div style={{ color: "#64748b", fontSize: "14px" }}>الصفحة {currentPage} من {totalPages}</div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${!hasPrevious ? 'disabled' : ''}`}>
                <button onClick={() => handlePageChange(currentPage - 1)} style={{ ...paginationBtnStyle, cursor: !hasPrevious ? "not-allowed" : "pointer", opacity: !hasPrevious ? 0.5 : 1 }}>السابق</button>
              </li>
              {getPageNumbers().map((page, idx) => (
                <li key={idx}>
                  {page === '...' ? <span style={{ ...paginationBtnStyle, cursor: "default" }}>...</span> :
                    <button onClick={() => handlePageChange(page)} style={{ ...paginationBtnStyle, background: currentPage === page ? "#2563eb" : "#fff", color: currentPage === page ? "#fff" : "#475569", fontWeight: currentPage === page ? "600" : "500" }}>{page}</button>}
                </li>
              ))}
              <li className={`page-item ${!hasNext ? 'disabled' : ''}`}>
                <button onClick={() => handlePageChange(currentPage + 1)} style={{ ...paginationBtnStyle, cursor: !hasNext ? "not-allowed" : "pointer", opacity: !hasNext ? 0.5 : 1 }}>التالي</button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}

// Styles
const labelStyle = { color: "#475569", fontSize: 14, fontWeight: 600, marginBottom: 6, display: "block" };
const thStyle = { color: "#1e40af", fontSize: 14, padding: "12px 8px", fontWeight: 600, border: "1px solid #bfdbfe" };
const tdStyle = { padding: "12px 8px", border: "1px solid #e0e7ff", fontSize: 16, fontWeight: 600, color: "#475569" };
const selectStyle = { width: "100%", padding: "10px 12px", border: "1px solid #bfdbfe", borderRadius: 6, fontSize: 15, color: "#475569", outline: "none", cursor: "pointer", background: "#fff" };
const buttonStyle = (color) => ({ background: color, color: "#fff", padding: "10px 16px", borderRadius: 6, border: "none", fontWeight: 600, fontSize: 14, cursor: "pointer" });
const tableStyle = { border: "1px solid #dbeafe", borderRadius: 8, overflow: "hidden", boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)", textAlign: "center" };
const actionBtnStyle = (color) => ({ background: color, color: "#fff", padding: "6px 12px", borderRadius: 6, border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer", transition: "all 0.3s ease" });
const paginationBtnStyle = { padding: "8px 14px", margin: "0 3px", border: "1px solid #bfdbfe", borderRadius: 6, fontSize: 14, cursor: "pointer", transition: "all 0.2s ease" };